import { ComponentType, lazy } from "react";

const LandingPage = lazy(() => import("../pages/LandingPage/LandingPage"));
const LoginPage = lazy(() => import("../pages/LoginPage/LoginPage"));
const RegisterPage = lazy(() => import("../pages/RegisterPage/RegisterPage"));
const ForgotPasswordPage = lazy(
  () => import("../pages/ForgotPasswordPage/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(
  () => import("../pages/ResetPasswordPage/ResetPasswordPage"),
);
const HomePage = lazy(() => import("../pages/HomePage/HomePage"));
const QuizPage = lazy(() => import("../pages/QuizPage/QuizPage"));
const LeaderBoardPage = lazy(
  () => import("../pages/LeaderBoardPage/LeaderBoardPage"),
);
const ProfilePage = lazy(() => import("../pages/ProfilePage/ProfilePage"));
const SettingsPage = lazy(() => import("../pages/SettingsPage/SettingsPage"));
const ShopPage = lazy(() => import("../pages/ShopPage/ShopPage"));

export interface Routes {
  path: string;
  page: ComponentType;
  isPrivate: boolean;
}

export const routes: Routes[] = [
  {
    path: "/",
    page: LandingPage,
    isPrivate: false,
  },
  {
    path: "/login",
    page: LoginPage,
    isPrivate: false,
  },
  {
    path: "/register",
    page: RegisterPage,
    isPrivate: false,
  },
  {
    path: "/forgot-password",
    page: ForgotPasswordPage,
    isPrivate: false,
  },
  {
    path: "/reset-password",
    page: ResetPasswordPage,
    isPrivate: false,
  },
  {
    path: "/home",
    page: HomePage,
    isPrivate: true,
  },
  {
    path: "/quiz/:quizId",
    page: QuizPage,
    isPrivate: true,
  },
  {
    path: "/leaderboard",
    page: LeaderBoardPage,
    isPrivate: true,
  },
  {
    path: "/profile",
    page: ProfilePage,
    isPrivate: true,
  },
  {
    path: "/settings",
    page: SettingsPage,
    isPrivate: true,
  },
  {
    path: "/shop",
    page: ShopPage,
    isPrivate: true,
  },
];

export const isPublicRoute = (path: string): boolean => {
  // Sort routes by specificity (longer paths first) to match most specific route first
  const sortedRoutes = [...routes].sort((a, b) => {
    // Exact matches first
    if (a.path === path && b.path !== path) return -1;
    if (b.path === path && a.path !== path) return 1;
    // Then by path length (longer = more specific)
    return b.path.length - a.path.length;
  });

  const route = sortedRoutes.find((r) => {
    // Exact match
    if (r.path === path) return true;

    // For parameterized routes (e.g., /quiz/:quizId), check if path starts with base path
    if (r.path.includes(":")) {
      const basePath = r.path.split(":")[0];
      return path.startsWith(basePath);
    }

    // For non-parameterized routes, check if path starts with route + "/"
    // This matches subpaths like /login/something to /login
    return path.startsWith(r.path + "/");
  });
  return route ? !route.isPrivate : false;
};

export const isPrivateRoute = (path: string): boolean => {
  // Sort routes by specificity (longer paths first) to match most specific route first
  const sortedRoutes = [...routes].sort((a, b) => {
    // Exact matches first
    if (a.path === path && b.path !== path) return -1;
    if (b.path === path && a.path !== path) return 1;
    // Then by path length (longer = more specific)
    return b.path.length - a.path.length;
  });

  const route = sortedRoutes.find((r) => {
    // Exact match
    if (r.path === path) return true;

    // For parameterized routes (e.g., /quiz/:quizId), check if path starts with base path
    if (r.path.includes(":")) {
      const basePath = r.path.split(":")[0];
      return path.startsWith(basePath);
    }

    // For non-parameterized routes, check if path starts with route + "/"
    // This matches subpaths like /home/something to /home
    return path.startsWith(r.path + "/");
  });
  return route ? route.isPrivate : false;
};
